import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

export const nameAudioFile = "audio";
export const nameAudioExtension = "webm";

const AUDIO_MIME_TYPES = [
  { mimeType: "audio/webm;codecs=opus", extension: "webm" },
  { mimeType: "audio/webm", extension: "webm" },
  { mimeType: "audio/mp4", extension: "m4a" },
  { mimeType: "audio/ogg;codecs=opus", extension: "ogg" },
];

const getSupportedAudioType = () => {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return null;
  return AUDIO_MIME_TYPES.find(({ mimeType }) => MediaRecorder.isTypeSupported(mimeType)) ?? null;
};

const getAudioExtension = (mimeType, fallback) => {
  const supported = AUDIO_MIME_TYPES.find((item) => item.mimeType === mimeType);
  if (supported) return supported.extension;
  if (mimeType?.includes("mp4")) return "m4a";
  if (mimeType?.includes("ogg")) return "ogg";
  if (mimeType?.includes("webm")) return "webm";
  return fallback;
};

const useAudioRecorder = ({
  nameAudioFile: customNameAudioFile = nameAudioFile,
  nameAudioExtension: customNameAudioExtension = nameAudioExtension,
} = {}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState("");
  const [audioFile, setAudioFile] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunks = useRef([]);
  const isStoppingRef = useRef(false);
  const audioUrlRef = useRef("");

  const stopStreamTracks = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => {
      try {
        track.stop();
      } catch (error) {}
    });
    streamRef.current = null;
  }, []);

  const revokeAudioUrl = useCallback(() => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = "";
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      revokeAudioUrl();
      setAudioURL("");
      setAudioFile(null);
      audioChunks.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const supportedAudioType = getSupportedAudioType();
      const mediaRecorder = new MediaRecorder(
        stream,
        supportedAudioType ? { mimeType: supportedAudioType.mimeType } : undefined,
      );

      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;
      audioChunks.current = [];
      isStoppingRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const mimeType = mediaRecorder.mimeType || supportedAudioType?.mimeType || "audio/webm";
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        const extension = getAudioExtension(mimeType, customNameAudioExtension);

        if (audioBlob.size > 0) {
          const file = new File(
            [audioBlob],
            `${customNameAudioFile}_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.${extension}`,
            {
              type: mimeType,
              lastModified: Date.now(),
            },
          );

          const url = URL.createObjectURL(audioBlob);

          revokeAudioUrl();
          audioUrlRef.current = url;
          setAudioFile(file);
          setAudioURL(url);
        }
        setIsRecording(false);

        audioChunks.current = [];
        mediaRecorderRef.current = null;
        isStoppingRef.current = false;
        stopStreamTracks();
      };

      mediaRecorder.onerror = () => {
        setIsRecording(false);
        audioChunks.current = [];
        mediaRecorderRef.current = null;
        isStoppingRef.current = false;
        stopStreamTracks();
      };

      mediaRecorder.start(250);
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      throw error;
    }
  }, [customNameAudioExtension, customNameAudioFile, revokeAudioUrl, stopStreamTracks]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      isRecording &&
      mediaRecorderRef.current.state !== "inactive" &&
      !isStoppingRef.current
    ) {
      isStoppingRef.current = true;

      try {
        mediaRecorderRef.current.requestData?.();
      } catch (error) {}

      mediaRecorderRef.current.stop();
    }
  }, [isRecording]);

  const resetRecording = useCallback(() => {
    revokeAudioUrl();

    setAudioURL("");
    setAudioFile(null);
    audioChunks.current = [];
  }, [revokeAudioUrl]);

  useEffect(() => {
    return () => {
      try {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
      } catch (error) {}

      stopStreamTracks();
      revokeAudioUrl();
    };
  }, [revokeAudioUrl, stopStreamTracks]);

  return {
    isRecording,
    audioURL,
    audioFile,
    startRecording,
    stopRecording,
    resetRecording,
    hasAudio: !!audioURL,
  };
};

export default useAudioRecorder;
